import React, { useState, useRef } from 'react';
import { parseMenuTextLocally } from '../utils/localMenuParser';
import { formatParsedMenu } from '../utils/menuTextFormat';

export default function MenuSourceCard({ source, index, onUpdate, onRemove }) {
  const [textInput, setTextInput] = useState('');
  const fileRef = useRef(null);

  const handlePhotoUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    onUpdate(source.id, {
      inputType: 'photo',
      status: 'parsing',
      label: source.label || `Menu ${index + 1}`
    });

    try {
      const formData = new FormData();
      formData.append('image', file);

      const res = await fetch('/ocr-and-parse', {
        method: 'POST',
        body: formData
      });

      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.error || 'Failed to process image');
      }

      const data = await res.json();
      onUpdate(source.id, {
        rawText: data.rawText || '',
        categories: formatParsedMenu(data.menu || { categories: [] }).categories || [],
        status: 'done'
      });
    } catch (err) {
      onUpdate(source.id, { status: 'error' });
      const msg = err.message?.includes('Failed to fetch') || err.message?.includes('NetworkError')
        ? 'Cannot reach the backend server. Make sure the API server is running on port 3001 (run "npm run dev" from the project root).'
        : err.message;
      alert(`Error: ${msg}`);
    }
  };

  const handleTextParse = async () => {
    if (!textInput.trim()) return;

    onUpdate(source.id, {
      inputType: 'text',
      rawText: textInput,
      status: 'parsing',
      label: source.label || `Menu ${index + 1}`
    });

    try {
      const res = await fetch('/parse', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ text: textInput })
      });

      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.error || 'Failed to parse text');
      }

      const data = await res.json();
      onUpdate(source.id, {
        categories: formatParsedMenu(data.menu || { categories: [] }).categories || [],
        status: 'done'
      });
    } catch (err) {
      const isNetworkError = err.message?.includes('Failed to fetch') || err.message?.includes('NetworkError');
      try {
        const localMenu = parseMenuTextLocally(textInput);
        onUpdate(source.id, {
          categories: localMenu.categories,
          status: 'done'
        });
        if (isNetworkError) {
          alert('MenuTide is currently having trouble reaching the server. Using local (offline) parsing as a fallback.');
        } else {
          console.error('AI Parser Error:', err.message);
          alert('The AI service is currently at capacity. MenuTide used local parsing for this text as a fallback.');
        }
      } catch (fallbackErr) {
        onUpdate(source.id, { status: 'error' });
        const msg = isNetworkError
          ? 'Cannot reach the backend server. Make sure the API server is running on port 3001 (run "npm run dev" from the project root).'
          : err.message;
        alert(`Error: ${msg}`);
      }
    }
  };

  const totalItems = source.categories.reduce((sum, c) => sum + c.items.length, 0);

  return (
    <div className={`source-card ${source.status}`}>
      <div className="source-header">
        <input
          className="source-label-input"
          type="text"
          placeholder={`Menu ${index + 1} (e.g., Dinner, Desserts)`}
          value={source.label}
          onChange={e => onUpdate(source.id, { label: e.target.value })}
        />
        {onRemove && (
          <button className="source-remove-btn" onClick={onRemove} title="Remove">
            ✕
          </button>
        )}
      </div>

      {source.status === 'done' ? (
        <div className="source-done">
          <div className="source-done-icon">✅</div>
          <div className="source-done-info">
            <strong>{source.categories.length} {source.categories.length === 1 ? 'category' : 'categories'}</strong>
            <span>{totalItems} {totalItems === 1 ? 'item' : 'items'} parsed</span>
          </div>
          <button
            className="secondary-btn"
            style={{ padding: '6px 12px', fontSize: '0.85rem' }}
            onClick={() => onUpdate(source.id, { status: 'pending', categories: [], rawText: '' })}
          >
            ← Back to Upload/Paste
          </button>
        </div>
      ) : source.status === 'parsing' ? (
        <div className="source-parsing">
          <div className="parsing-spinner"></div>
          <span>Processing with AI...</span>
        </div>
      ) : source.status === 'error' ? (
        <div className="source-error">
          <span>⚠️ Something went wrong.</span>
          <button
            className="source-redo-btn"
            onClick={() => onUpdate(source.id, { status: 'pending', categories: [], rawText: '' })}
          >
            Try Again
          </button>
        </div>
      ) : (
        <div className="source-inputs">
          <div className="source-input-options">
            {/* Photo upload */}
            <div
              className="source-option upload-option"
              onClick={() => fileRef.current?.click()}
            >
              <input
                ref={fileRef}
                type="file"
                accept="image/*"
                onChange={handlePhotoUpload}
                style={{ display: 'none' }}
              />
              <div className="option-icon">📷</div>
              <div className="option-label">Upload Photo</div>
              <div className="option-hint">Snap a photo of handwritten notes or menu printout</div>
            </div>

            <div className="source-divider">
              <span>or</span>
            </div>

            {/* Text paste */}
            <div className="source-option text-option">
              <div className="text-option-header">
                <div className="text-option-title">
                  <span className="option-icon-mini">📝</span>
                  <strong className="option-label-mini">Paste Text</strong>
                </div>
                {textInput.trim() && (
                  <button className="parse-btn primary-btn" onClick={handleTextParse}>
                    Parse Menu →
                  </button>
                )}
              </div>
              <textarea
                className="source-textarea"
                placeholder={"Paste your menu text here...\n\nExample:\nStarters\nFrench Onion Soup $14\nCaesar Salad $12\n\nMains\nGrilled Salmon $38\nNY Strip Steak $52"}
                value={textInput}
                onChange={e => setTextInput(e.target.value)}
                rows={8}
              />
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
